/** Localized copy per notification category code — one map, every surface that names
 *  a category.
 *
 *  The codes are the backend's: the value set of `notifications.senders`'
 *  `_CATEGORY_BY_EVENT`, read by `_category_for_sender` in that module: the Teams
 *  delivery gate (`msteams.deliveries`) imports it directly, the card renderer
 *  (`msteams.cards`) through its `render_api.category_for_sender` alias.
 *
 *  **One map because a category is one fact, however many switches read it.** Profile
 *  → Notifications now names each code twice on the same row — once for the bell and
 *  email, once for Microsoft Teams (NG-1187) — and the workspace's Teams allowlist,
 *  which used to name them a second time in the Integration Library, is gone. The
 *  allowlist is why this module exists: it had a label set of its own, which is how
 *  "Collaboration" in one place became "Action required" in the other for the same
 *  `collaboration` code, and no amount of care at the call sites fixes that while the
 *  strings are separate keys.
 *
 *  So the label is a property of the *code*, held once here. Keys rather than
 *  translated strings, because this module has no `t` and callers do.
 *
 *  `hintKey` is the one-line explanation of what the category covers. It is rendered
 *  once per row, not once per switch: the two switches on a row are two destinations
 *  for the same events, so repeating the sentence beside each would suggest they cover
 *  different things.
 *
 *  A code absent from this map is not an error: the Profile form falls back to the
 *  English `label` the API returns for it, so a category the backend adds degrades to
 *  the server's wording rather than rendering blank — under the form's trailing
 *  "Other" heading, which is what stops a new backend category from being silently
 *  hidden. `report` is the standing case: it has never had copy here, so the Profile
 *  row reads "Report runs" from the API and its two switches work like any other's.
 *  Giving it an entry is a copy decision, not a bug fix.
 */
/** The `NotificationType.code` the **Monday summary** rides on, seeded by the backend's
 *  `db.0412` and named there as `weekly_digest.DIGEST_CATEGORY`.
 *
 *  Exported because the Profile form has to single this category out by name (NG-1641):
 *  its "Send my Monday summary now" button belongs to *this* category, not to the
 *  `teams_only` **shape**. `msteams.preferences.TEAMS_ONLY_CATEGORIES` is a set designed
 *  to grow, so gating on the row's `teams_only` flag would draw a second button, with an
 *  identical name, the day a second Teams-only category ships — two controls answering to
 *  one accessible name (NG-847) and both sending the digest.
 *
 *  One spelling, used as this map's own key as well as at that call site, so the string
 *  cannot be typed twice and drift once.
 */
export const WEEKLY_SUMMARY_CODE = "weekly_summary";

export const NOTIFICATION_CATEGORY_I18N: Record<string, { labelKey: string; hintKey: string }> = {
  status_change: {
    labelKey: "profile_settings.notifications.status_change",
    hintKey: "profile_settings.notifications.status_change_hint",
  },
  field_change: {
    labelKey: "profile_settings.notifications.field_change",
    hintKey: "profile_settings.notifications.field_change_hint",
  },
  comment: {
    labelKey: "profile_settings.notifications.comment",
    hintKey: "profile_settings.notifications.comment_hint",
  },
  mention: {
    labelKey: "profile_settings.notifications.mention",
    hintKey: "profile_settings.notifications.mention_hint",
  },
  deletion: {
    labelKey: "profile_settings.notifications.deletion",
    hintKey: "profile_settings.notifications.deletion_hint",
  },
  assignment: {
    labelKey: "profile_settings.notifications.assignment",
    hintKey: "profile_settings.notifications.assignment_hint",
  },
  collaboration: {
    labelKey: "profile_settings.notifications.collaboration",
    hintKey: "profile_settings.notifications.collaboration_hint",
  },
  account: {
    labelKey: "profile_settings.notifications.account",
    hintKey: "profile_settings.notifications.account_hint",
  },
  sla: {
    labelKey: "profile_settings.notifications.sla",
    hintKey: "profile_settings.notifications.sla_hint",
  },
  time_off_conflict: {
    labelKey: "profile_settings.notifications.time_off_conflict",
    hintKey: "profile_settings.notifications.time_off_conflict_hint",
  },
  shared_dashboard: {
    labelKey: "profile_settings.notifications.shared_dashboard",
    hintKey: "profile_settings.notifications.shared_dashboard_hint",
  },
  // Teams-only (NG-1641): it is delivered as a Teams card and nothing else, so the
  // Profile row carries the Teams switch alone and does not render at all for
  // somebody without a Teams link. Copy here all the same — the form's "Other"
  // heading is for codes with none, and this one has a deliberate section.
  [WEEKLY_SUMMARY_CODE]: {
    labelKey: "profile_settings.notifications.weekly_summary",
    hintKey: "profile_settings.notifications.weekly_summary_hint",
  },
};
